import React from 'react';
import { Bell, Check, CheckCheck, X, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationsContext';
import './NotificationsPanel.css';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ isOpen, onClose }) => {
  const { notifications, unreadCount, loading, acknowledgeNotification, acknowledgeAll } = useNotifications();

  if (!isOpen) return null;

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />;
      default:
        return <Info className="w-5 h-5 text-gray-500 flex-shrink-0" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleAcknowledge = async (e: React.MouseEvent, alertId: string) => {
    e.stopPropagation();
    try {
      await acknowledgeNotification(alertId);
    } catch (err) {
      console.error('Failed to acknowledge notification:', err);
    }
  };

  const handleAcknowledgeAll = async () => {
    try {
      await acknowledgeAll();
    } catch (err) {
      console.error('Failed to acknowledge all notifications:', err);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="notifications-panel-backdrop" 
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="notifications-panel">
        {/* Header - Compact */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Bell style={{ width: '18px', height: '18px', color: '#374151' }} />
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: 600, 
              color: '#111827',
              margin: 0
            }}>
              Notifications
            </h3>
            {unreadCount > 0 && (
              <span style={{
                padding: '2px 8px',
                fontSize: '11px',
                fontWeight: 500,
                backgroundColor: '#f97316',
                color: 'white',
                borderRadius: '12px'
              }}>
                {unreadCount}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {unreadCount > 0 && (
              <button
                onClick={handleAcknowledgeAll}
                style={{
                  fontSize: '11px',
                  color: '#2563eb',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: 0
                }}
              >
                <CheckCheck style={{ width: '12px', height: '12px' }} />
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                color: '#6b7280',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X style={{ width: '18px', height: '18px' }} />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          minHeight: 0
        }}>
          {loading && notifications.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>
              Loading notifications...
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>
              <Bell style={{ width: '48px', height: '48px', margin: '0 auto 8px', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No new notifications</p>
            </div>
          ) : (
            <div>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: '#eff6ff',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}
                >
                  {/* Icon */}
                  {getSeverityIcon(notification.severity)}
                  
                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ 
                      fontSize: '13px', 
                      color: '#111827',
                      margin: 0,
                      lineHeight: '1.4'
                    }}>
                      {notification.message}
                    </p>
                    <div style={{ 
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '11px',
                      color: '#6b7280'
                    }}>
                      <span>{formatTimestamp(notification.triggeredAt)}</span>
                      <span>•</span>
                      <span style={{ textTransform: 'capitalize' }}>
                        {notification.alertType?.replace(/_/g, ' ') || 'Notification'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Dismiss button */}
                  <button
                    onClick={(e) => handleAcknowledge(e, String(notification.id))}
                    style={{
                      flexShrink: 0,
                      color: '#9ca3af',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="Mark as read"
                  >
                    <Check style={{ width: '16px', height: '16px' }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationsPanel;
